import { useEffect, useMemo } from "react";
import { Archive, MessageCircle, Plus } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { ConversationRow } from "@/components/messages/ConversationRow";
import { useCity } from "@/contexts/CityContext";
import { track } from "@/lib/analytics";
import {
  messageErrorMessage,
  sortConversationsUnreadFirst,
  useMessageConversations,
  useUpdateMessageConversation,
  type MessageConversationFilter,
} from "@/lib/messages";
import "@/styles/messages.css";

export default function Messages() {
  const { city } = useCity();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter: MessageConversationFilter =
    searchParams.get("filter") === "archived" ? "archived" : "inbox";
  const conversations = useMessageConversations(city.slug, filter);
  const updateConversation = useUpdateMessageConversation(city.slug);
  const rows = useMemo(
    () =>
      sortConversationsUnreadFirst(
        conversations.data?.pages.flatMap((page) => page.conversations) ?? [],
      ),
    [conversations.data],
  );

  useEffect(() => track("messages_view"), []);

  const setFilter = (next: MessageConversationFilter) => {
    setSearchParams(next === "archived" ? { filter: "archived" } : {}, {
      replace: true,
    });
  };

  return (
    <section className="pb-messages-page" aria-labelledby="messages-heading">
      <header className="pb-messages-page-heading">
        <div>
          <p className="pb-board-eyebrow">Private colleague conversations</p>
          <h1 id="messages-heading">Messages</h1>
          <p>Talk directly with message-enabled clinicians in {city.name}, without patient details.</p>
        </div>
        <Link className="pb-messages-primary" to="/board/messages/new">
          <Plus size={15} aria-hidden="true" />New message
        </Link>
      </header>

      <div className="pb-messages-filter" aria-label="Conversation filter">
        <button
          type="button"
          aria-pressed={filter === "inbox"}
          onClick={() => setFilter("inbox")}
        >
          <MessageCircle size={14} aria-hidden="true" />Inbox
        </button>
        <button
          type="button"
          aria-pressed={filter === "archived"}
          onClick={() => setFilter("archived")}
        >
          <Archive size={14} aria-hidden="true" />Archived
        </button>
      </div>

      {conversations.isLoading ? (
        <div className="pb-messages-state" role="status">Loading conversations…</div>
      ) : conversations.isError ? (
        <div className="pb-messages-state pb-messages-state--error" role="alert">
          <span>Conversations could not be loaded.</span>
          <button type="button" onClick={() => void conversations.refetch()}>Retry</button>
        </div>
      ) : rows.length ? (
        <div className="pb-messages-conversations">
          {rows.map((conversation) => (
            <ConversationRow
              key={conversation.id}
              conversation={conversation}
              pending={
                updateConversation.isPending &&
                updateConversation.variables?.conversationId === conversation.id
              }
              onUpdate={(conversationId, patch) =>
                updateConversation.mutate({ conversationId, ...patch })
              }
            />
          ))}
          {updateConversation.isError ? (
            <p className="pb-messages-form-error" role="alert">
              {messageErrorMessage(updateConversation.error)}
            </p>
          ) : null}
          {conversations.hasNextPage ? (
            <button
              className="pb-messages-load-more"
              type="button"
              disabled={conversations.isFetchingNextPage}
              onClick={() => void conversations.fetchNextPage()}
            >
              {conversations.isFetchingNextPage ? "Loading…" : "Load more conversations"}
            </button>
          ) : null}
        </div>
      ) : (
        <div className="pb-messages-empty">
          <MessageCircle size={24} aria-hidden="true" />
          <p>{filter === "archived" ? "You have no archived conversations." : "Your inbox is ready for a private colleague conversation."}</p>
          <Link className="pb-messages-primary" to="/board/messages/new">Find a colleague</Link>
        </div>
      )}
    </section>
  );
}
